/**
 * AiSystemsTab — AI-in-clinical-decisions oversight (Phase 5).
 *
 * Per-vendor AI inventory with the red-flag engine and state-law matrix:
 *   GET  /api/fdr/entities/:id/ai-systems
 *   POST /api/fdr/entities/:id/ai-systems
 *   POST /api/fdr/ai-systems/:id/attest      (append-only, 10-yr retention)
 *
 * First-to-market module: 42 CFR 422.101(c) + CMS Feb 2024 FAQ, OIG MA ICPG
 * vendor-AI review, and the 8 enacted state URA laws.
 */
import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL;

const AI_FUNCTIONS = ['utilization_management', 'prior_authorization', 'claims_adjudication', 'downcoding', 'care_termination', 'other'];
const AI_ROLES = [
  ['recommend_only', 'Recommend only'], ['first_pass_screen', 'First-pass screen'],
  ['auto_approve_only', 'Auto-approve only (never denies)'], ['adverse_capable', 'Adverse-capable'],
];
const FLAG_CFG = {
  critical: 'bg-red-50 text-red-700 border-red-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
};
const inputCls = 'w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400';
const btnPrimary = 'px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-orange-500/25 disabled:opacity-50';

function Card({ children, className = '' }) {
  return <div className={`bg-white rounded-2xl border border-slate-200/60 shadow-sm ${className}`}>{children}</div>;
}

export default function AiSystemsTab({ entityId }) {
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [attestFor, setAttestFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/fdr/entities/${entityId}/ai-systems`, { credentials: 'include' });
      const d = await res.json();
      if (d.success) setSystems(d.systems || []);
    } catch {}
    setLoading(false);
  }, [entityId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="p-12 flex items-center justify-center">
      <div className="w-8 h-8 rounded-full border-2 border-slate-200 border-t-orange-500 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {systems.length} AI/algorithmic system{systems.length !== 1 ? 's' : ''} in clinical or payment decisions —
          42 CFR 422.101(c), OIG ICPG vendor-AI review, 8 enacted state laws
        </p>
        <button onClick={() => setShowAdd(true)}
          className="px-3.5 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-sm hover:shadow transition-all">
          Register AI System
        </button>
      </div>

      {systems.length === 0 ? (
        <Card className="p-10 text-center">
          <p className="text-sm text-slate-500 font-medium">No AI systems registered.</p>
          <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
            If this vendor uses algorithms in UM, prior authorization, claims, or coding decisions, register each one —
            the OIG ICPG expects the plan to review vendor AI tools, and eight state laws now regulate them.
          </p>
        </Card>
      ) : systems.map(s => (
        <Card key={s.id} className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-800">{s.name}</span>
                {s.developer && <span className="text-xs text-slate-400">by {s.developer}</span>}
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 capitalize">{(s.model_type || 'model').replace(/_/g, ' ')}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${s.decision_role === 'adverse_capable' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                  {s.decision_role.replace(/_/g, ' ')}
                </span>
                <span className="text-xs text-slate-400 capitalize">{s.decision_function.replace(/_/g, ' ')}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                States: {(Array.isArray(s.states) ? s.states : []).join(', ') || '—'} ·
                Override rate: {s.override_rate_pct ?? '—'}% ·
                Bias testing: {s.bias_testing_on_file ? 'on file' : 'none'} ·
                Per-decision logs: {s.audit_logs_producible ? 'producible' : 'unconfirmed'}
              </div>
              {s.latest_attestation ? (
                <div className="text-xs text-emerald-700 mt-1">
                  CY{s.latest_attestation.attestation_year} attestation:
                  HITL {s.latest_attestation.attests_human_in_loop ? '✓' : '✗'} ·
                  individualized {s.latest_attestation.attests_individualized ? '✓' : '✗'} ·
                  non-discrimination {s.latest_attestation.attests_no_discrimination ? '✓' : '✗'} —
                  {' '}{s.latest_attestation.attested_by_name}, {s.latest_attestation.attested_by_title}
                </div>
              ) : (
                <div className="text-xs text-amber-700 mt-1">No attestation on record.</div>
              )}
            </div>
            <button onClick={() => setAttestFor(s)}
              className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-orange-700 bg-orange-50 hover:bg-orange-100 whitespace-nowrap transition-colors flex-shrink-0">
              Record attestation
            </button>
          </div>

          {s.flags?.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {s.flags.map((f, i) => (
                <div key={i} className={`text-xs border rounded-xl px-3 py-2 ${FLAG_CFG[f.severity] || FLAG_CFG.info}`}>
                  <span className="font-bold uppercase text-[10px] tracking-wide mr-1.5">{f.severity}</span>{f.note}
                </div>
              ))}
            </div>
          )}

          {s.applicable_laws?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Applicable state laws</div>
              <div className="space-y-1">
                {s.applicable_laws.map(l => (
                  <div key={l.state + l.law} className="text-xs text-slate-600">
                    <span className={`font-bold ${l.in_effect ? 'text-slate-800' : 'text-amber-600'}`}>
                      {l.state} {l.law} {l.in_effect ? '(in effect)' : `(effective ${l.effective})`}
                    </span> — {l.rule}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      ))}

      {showAdd && <RegisterAiModal entityId={entityId} onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); }} />}
      {attestFor && <AiAttestModal system={attestFor} onClose={() => setAttestFor(null)} onDone={() => { setAttestFor(null); load(); }} />}
    </div>
  );
}

function RegisterAiModal({ entityId, onClose, onDone }) {
  const [form, setForm] = useState({
    name: '', developer: '', model_type: 'ml_predictive', decision_function: 'utilization_management',
    decision_role: 'first_pass_screen', states: '', override_rate_pct: '',
    bias_testing_on_file: false, audit_logs_producible: false, material_change_notice: false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      const body = {
        ...form,
        states: form.states.split(',').map(x => x.trim().toUpperCase()).filter(Boolean),
        override_rate_pct: form.override_rate_pct === '' ? null : Number(form.override_rate_pct),
      };
      const res = await fetch(`${API}/api/fdr/entities/${entityId}/ai-systems`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await res.json();
      if (d.success) onDone(); else setErr(d.message || 'Failed');
    } catch { setErr('Failed to register'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-8 py-5 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-800">Register AI System</h3>
          <p className="text-xs text-slate-400 mt-0.5">Every algorithm touching UM, prior auth, claims, or coding decisions</p>
        </div>
        <form onSubmit={submit} className="px-8 py-5 space-y-4">
          {err && <div className="text-red-600 text-sm bg-red-50 rounded-xl px-4 py-2">{err}</div>}
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="System name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className={inputCls} />
            <input placeholder="Developer" value={form.developer} onChange={e => setForm({ ...form, developer: e.target.value })} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select value={form.decision_function} onChange={e => setForm({ ...form, decision_function: e.target.value })} className={inputCls}>
              {AI_FUNCTIONS.map(f => <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>)}
            </select>
            <select value={form.decision_role} onChange={e => setForm({ ...form, decision_role: e.target.value })} className={inputCls}>
              {AI_ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="States (e.g. FL, TX, CA)" value={form.states} onChange={e => setForm({ ...form, states: e.target.value })} className={inputCls} />
            <input type="number" step="0.1" placeholder="Human override rate %" value={form.override_rate_pct}
              onChange={e => setForm({ ...form, override_rate_pct: e.target.value })} className={inputCls} />
          </div>
          <div className="space-y-1.5 text-sm text-slate-700">
            {[['bias_testing_on_file', 'Bias / performance testing evidence on file'],
              ['audit_logs_producible', 'Per-decision audit logs producible on demand'],
              ['material_change_notice', 'Contract requires notice of material model change']].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form[k]} onChange={e => setForm({ ...form, [k]: e.target.checked })}
                  className="rounded border-slate-300 text-orange-600 focus:ring-orange-500/30" />
                {l}
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Registering…' : 'Register'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AiAttestModal({ system, onClose, onDone }) {
  const [form, setForm] = useState({
    attests_human_in_loop: false, attests_individualized: false, attests_no_discrimination: false,
    clinical_reviewer_roster: '', attested_by_name: '', attested_by_title: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const STATEMENTS = [
    ['attests_human_in_loop', 'No adverse determination is made solely (in Texas, wholly or partly) by this system — a licensed clinician exercising independent judgment makes final adverse decisions.'],
    ['attests_individualized', 'Determinations rest on the individual enrollee medical history, physician recommendations, and clinical notes — not group datasets (42 CFR 422.101(c)).'],
    ['attests_no_discrimination', 'The system does not discriminate on any basis prohibited by Section 1557.'],
  ];

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      const res = await fetch(`${API}/api/fdr/ai-systems/${system.id}/attest`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const d = await res.json();
      if (d.success) onDone(); else setErr(d.message || 'Failed');
    } catch { setErr('Failed to record'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
        <div className="px-8 py-5 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-800">AI Governance Attestation</h3>
          <p className="text-xs text-slate-400 mt-0.5">{system.name} · append-only, 10-year retention</p>
        </div>
        <form onSubmit={submit} className="px-8 py-5 space-y-4">
          {err && <div className="text-red-600 text-sm bg-red-50 rounded-xl px-4 py-2">{err}</div>}
          <div className="space-y-2 text-sm text-slate-700">
            {STATEMENTS.map(([k, l]) => (
              <label key={k} className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={form[k]} onChange={e => setForm({ ...form, [k]: e.target.checked })}
                  className="mt-0.5 rounded border-slate-300 text-orange-600 focus:ring-orange-500/30" />
                <span>{l}</span>
              </label>
            ))}
          </div>
          <textarea rows={2} placeholder="Named clinical reviewer roster (licensed reviewers making adverse determinations)"
            value={form.clinical_reviewer_roster} onChange={e => setForm({ ...form, clinical_reviewer_roster: e.target.value })} className={inputCls} />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Attested by (name) *" value={form.attested_by_name} onChange={e => setForm({ ...form, attested_by_name: e.target.value })} required className={inputCls} />
            <input placeholder="Title *" value={form.attested_by_title} onChange={e => setForm({ ...form, attested_by_title: e.target.value })} required className={inputCls} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Recording…' : 'Record Attestation'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
